import React, { ReactNode, PureComponent, ComponentType } from 'react';
import { T } from 'app/components/localization';
import {
  MULTI_SELECT_NEGATIVE,
  INFO_BACKGROUND_COLOR,
  MULTI_SELECT_POSITIVE,
  ALERT_TEXT_COLOR,
} from 'app/constants/Colors';
import { createStyles } from 'app/utils/css';
import { BORDER_RADIUS_A } from 'app/constants/Styles';
import { getNameFunctionType } from 'app/services/localization';
import BaseButton from 'app/components/common/BaseButton';
import X from 'app/components/svgs/X';

export enum ValueVariant {
  Default = 'default',
  Positive = 'positive',
  Negative = 'negative',
}

interface IValueProps<ValueType> {
  children?: ReactNode;
  disabled?: boolean;
  onRemove: (value: ValueType) => unknown;
  value: ValueType;
  variant?: ValueVariant;
}

export type ValueComponent<ValueType> = ComponentType<IValueProps<ValueType>>;

/**
 * Helper component for creating the valueComponent property of MultiSelect.
 */
export class Value<ValueType> extends PureComponent<IValueProps<ValueType>> {
  public render() {
    const { children, variant, value, disabled } = this.props;
    return (
      <Styles>{(css) =>
        <div key={String(value)} className={css('container', { variant })}>
          <BaseButton className={css('closeButton')} onClick={this.onRemove} disabled={disabled}>
            <X color={ALERT_TEXT_COLOR} />
          </BaseButton>
          <span className={css('children')}>{children}</span>
        </div>
      }</Styles>
    );
  }

  private onRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    return this.props.onRemove(this.props.value);
  };
}

export interface IEnumOption {
  id: string;
  getName: getNameFunctionType;
  getDescription?: getNameFunctionType;
}

export const EnumValue = ({ value, ...props }: IValueProps<IEnumOption>) => (
  <T>{t =>
    <Value value={value} {...props}>{value.getName(t)}</Value>
  }</T>
);

export const TextValue = ({ value, ...props }: IValueProps<string>) => (
  <Value value={value} {...props}>{value}</Value>
);

const Styles = createStyles({
  container: ({ variant }) => ({
    border: `1px solid ${ALERT_TEXT_COLOR}`,
    borderRadius: BORDER_RADIUS_A,
    display: 'inline-block',
    color: ALERT_TEXT_COLOR,
    fontSize: '0.8em',
    marginRight: '5px',

    ':last-child': {
      marginRight: '0',
    },

    backgroundColor: (
      variant === ValueVariant.Positive ? MULTI_SELECT_POSITIVE :
      variant === ValueVariant.Negative ? MULTI_SELECT_NEGATIVE :
      /* else */ INFO_BACKGROUND_COLOR
    ),
  }),
  children: {
    display: 'inline-block',
    padding: '0.2em 0.4em',
    verticalAlign: 'middle',
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    borderRight: `1px solid ${ALERT_TEXT_COLOR}`,
    cursor: 'pointer',
    display: 'inline-flex',
    boxSizing: 'content-box',
    width: '0.5em',
    padding: '0.7em 0.4em',
    verticalAlign: 'middle',
  },
});
